/**
 * SecurityLogger.js
 *
 * Web equivalent of Android's SecurityLogger.kt and SecureLogger.kt
 *
 * Features:
 * - PII sanitization for all log messages (masks emails, phone numbers, UIDs)
 * - Firebase Analytics event logging for security audit trail
 * - Production-safe: strips verbose logs in production builds
 * - Centralized logging for auth, payment, subscription, and security events
 * - Session fingerprinting for credential sharing detection
 * - Retry logic for critical backend log events
 * - Dev-only DebugConsole overlay for real-time event monitoring
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { logEvent } from 'firebase/analytics';
import { analytics } from '../firebase-config';

// ======================== PII SANITIZATION ========================

const PII_PATTERNS = [
    // Email addresses → masked
    { regex: /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g, replacement: '[EMAIL_REDACTED]' },
    // Phone numbers (Indian format: +91XXXXXXXXXX, 10-digit)
    { regex: /(\+91|91)?[\s-]?\d{10}/g, replacement: '[PHONE_REDACTED]' },
    // Firebase UIDs (28-char alphanumeric)
    { regex: /\b[a-zA-Z0-9]{28}\b/g, replacement: '[UID_REDACTED]' },
    // Payment IDs (order_xxx, pay_xxx patterns)
    { regex: /\b(order_|pay_|txn_)[a-zA-Z0-9]+/gi, replacement: '[PAYMENT_ID_REDACTED]' },
    // IP addresses
    { regex: /\b\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}\b/g, replacement: '[IP_REDACTED]' },
];

/**
 * Sanitize a string by masking PII patterns.
 * Mirrors Android's SecureLogger.sanitize()
 */
function sanitize(message) {
    if (typeof message !== 'string') {
        try {
            message = JSON.stringify(message);
        } catch {
            message = String(message);
        }
    }
    let sanitized = message;
    for (const { regex, replacement } of PII_PATTERNS) {
        sanitized = sanitized.replace(regex, replacement);
    }
    return sanitized;
}

// ======================== PRODUCTION CHECK ========================

const IS_PRODUCTION = process.env.NODE_ENV === 'production';

// ======================== SESSION FINGERPRINTING ========================

/**
 * Generates a non-PII session fingerprint for detecting credential sharing.
 * Does NOT collect unique identifiers — only device class metadata.
 */
function getSessionFingerprint() {
    try {
        return {
            screen: `${window.screen.width}x${window.screen.height}`,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'unknown',
            language: navigator.language || 'unknown',
            platform: navigator.platform || 'unknown',
            colorDepth: window.screen.colorDepth,
            touchSupport: navigator.maxTouchPoints > 0,
            standalone: window.matchMedia('(display-mode: standalone)').matches,
        };
    } catch {
        return { screen: 'unknown', timezone: 'unknown' };
    }
}

// ======================== DEBUG EVENT BUFFER ========================
// Stores recent events for the DebugConsole overlay (dev mode only)
const DEBUG_EVENT_BUFFER = [];
const MAX_DEBUG_EVENTS = 50;

function pushDebugEvent(eventName, params) {
    if (IS_PRODUCTION) return;
    DEBUG_EVENT_BUFFER.unshift({
        id: Date.now() + Math.random(),
        timestamp: new Date().toLocaleTimeString(),
        event: eventName,
        params: { ...params },
    });
    if (DEBUG_EVENT_BUFFER.length > MAX_DEBUG_EVENTS) {
        DEBUG_EVENT_BUFFER.pop();
    }
}

// ======================== SECURE CONSOLE LOGGING ========================

/**
 * SecureLog — replaces direct console.* calls throughout the app.
 *
 * In development: logs normally (with PII sanitization)
 * In production: suppresses debug/info, only logs warnings and errors (sanitized)
 */
export const SecureLog = {
    debug: (...args) => {
        if (!IS_PRODUCTION) {
            console.log('[DEBUG]', ...args.map(a => typeof a === 'string' ? sanitize(a) : a));
        }
    },

    info: (...args) => {
        if (!IS_PRODUCTION) {
            console.info('[INFO]', ...args.map(a => typeof a === 'string' ? sanitize(a) : a));
        }
    },

    warn: (...args) => {
        console.warn('[WARN]', ...args.map(a => typeof a === 'string' ? sanitize(a) : a));
    },

    error: (...args) => {
        // Always log errors, but sanitize PII
        const sanitizedArgs = args.map(a => {
            if (a instanceof Error) {
                return new Error(sanitize(a.message));
            }
            return typeof a === 'string' ? sanitize(a) : a;
        });
        console.error('[ERROR]', ...sanitizedArgs);
    },
};

// ======================== FIREBASE ANALYTICS EVENTS ========================

/**
 * Log an event to Firebase Analytics (production only).
 * Mirrors Android's SecurityLogger event categories.
 *
 * All params are automatically PII-sanitized before logging.
 */
function logSecurityEvent(eventName, params = {}) {
    if (!analytics) return;

    // Sanitize all string params
    const sanitizedParams = {};
    for (const [key, value] of Object.entries(params)) {
        if (typeof value === 'string') {
            sanitizedParams[key] = sanitize(value);
        } else {
            sanitizedParams[key] = value;
        }
    }

    try {
        logEvent(analytics, eventName, sanitizedParams);
    } catch {
        // Silently fail — analytics is non-critical
    }

    // Push to debug buffer for DebugConsole
    pushDebugEvent(eventName, sanitizedParams);
}

/**
 * Log event to backend (production only, best effort).
 */
/**
 * Log event to backend with single retry for critical events.
 * Critical events (PAYMENT_FAILED, csp_violation) get one retry after 2s.
 */
async function logToBackend(backendUrl, orderId, event, details = {}, status = 'INFO') {
    if (!backendUrl || !orderId) return;

    // Construct endpoint URL (handled similarly to Dashboard.js)
    let endpoint = backendUrl;
    if (endpoint.endsWith('/')) endpoint = endpoint.slice(0, -1);
    if (!endpoint.includes('/api/v2')) endpoint += '/api/v2';
    endpoint += '/log-client-event';

    const payload = {
        order_id: orderId,
        event: event,
        details: details,
        status: status,
    };

    const isCritical = status === 'ERROR' || event.includes('FAILED') || event.includes('csp_violation');

    const attemptFetch = async () => {
        await fetch(endpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
            keepalive: true // Ensure request survives page navigation
        });
    };

    try {
        await attemptFetch();
    } catch (e) {
        if (isCritical) {
            // [SECURITY] Single retry after 2s for critical events
            try {
                await new Promise(resolve => setTimeout(resolve, 2000));
                await attemptFetch();
            } catch (retryErr) {
                console.warn('[SecurityLogger] Critical event log failed after retry:', event);
            }
        }
        // Non-critical: silently fail — logging should not block user flow
    }
}

// ======================== AUTH EVENTS ========================

export const AuthEvents = {
    loginSuccess: (method) => {
        logSecurityEvent('login_success', {
            method, // 'google' | 'email'
            timestamp: Date.now(),
            ...getSessionFingerprint(), // [SECURITY] Fingerprint for sharing detection
        });
    },

    loginFailure: (method, errorCode) => {
        logSecurityEvent('login_failure', {
            method,
            error_code: errorCode || 'unknown',
            timestamp: Date.now(),
        });
    },

    logout: () => {
        logSecurityEvent('user_logout', {
            timestamp: Date.now(),
        });
    },

    signupBlocked: (reason) => {
        logSecurityEvent('signup_blocked', {
            reason,
            timestamp: Date.now(),
        });
    },

    passwordResetRequested: () => {
        logSecurityEvent('password_reset_requested', {
            timestamp: Date.now(),
        });
    },

    androidAccountBlocked: () => {
        logSecurityEvent('android_account_blocked', {
            timestamp: Date.now(),
        });
    },
};

// ======================== PAYMENT EVENTS ========================

export const PaymentEvents = {
    initiated: (billId, amount, orderId = null, backendUrl = null) => {
        logSecurityEvent('payment_initiated', {
            bill_id: billId,
            amount,
            order_id: orderId ? sanitize(orderId) : null,
            timestamp: Date.now(),
        });

        if (orderId && backendUrl) {
            logToBackend(backendUrl, orderId, 'PAYMENT_INITIATED', {
                bill_id: billId,
                amount: amount
            });
        }
    },

    healthCheckStarted: () => {
        logSecurityEvent('payment_health_check', {
            timestamp: Date.now(),
        });
    },

    sessionCreated: (orderId) => {
        logSecurityEvent('payment_session_created', {
            order_id: sanitize(orderId),
            timestamp: Date.now(),
        });
    },

    redirected: (orderId = null, backendUrl = null) => {
        logSecurityEvent('payment_redirected', {
            order_id: orderId ? sanitize(orderId) : null,
            timestamp: Date.now(),
        });

        if (orderId && backendUrl) {
            logToBackend(backendUrl, orderId, 'PAYMENT_REDIRECTED', {}, 'INFO');
        }
    },

    success: (orderId) => {
        logSecurityEvent('payment_success', {
            order_id: sanitize(orderId),
            timestamp: Date.now(),
        });
    },

    failed: (orderId, reason, backendUrl = null) => {
        logSecurityEvent('payment_failed', {
            order_id: sanitize(orderId),
            reason: reason || 'unknown',
            timestamp: Date.now(),
        });

        if (orderId && backendUrl) {
            logToBackend(backendUrl, orderId, 'PAYMENT_FAILED', { reason }, 'ERROR');
        }
    },

    pending: (orderId) => {
        logSecurityEvent('payment_pending', {
            order_id: sanitize(orderId),
            timestamp: Date.now(),
        });
    },

    cooldownBlocked: (billId) => {
        logSecurityEvent('payment_cooldown_blocked', {
            bill_id: billId,
            timestamp: Date.now(),
        });
    },

    serverOffline: () => {
        logSecurityEvent('payment_server_offline', {
            timestamp: Date.now(),
        });
    },
};

// ======================== SUBSCRIPTION EVENTS ========================

export const SubscriptionEvents = {
    paywallShown: () => {
        logSecurityEvent('subscription_paywall_shown', {
            timestamp: Date.now(),
        });
    },

    qrScanStarted: () => {
        logSecurityEvent('subscription_qr_scan_started', {
            timestamp: Date.now(),
        });
    },

    qrScanSuccess: () => {
        logSecurityEvent('subscription_qr_scan_success', {
            timestamp: Date.now(),
        });
    },

    qrScanFailed: (reason) => {
        logSecurityEvent('subscription_qr_scan_failed', {
            reason: reason || 'unknown',
            timestamp: Date.now(),
        });
    },

    activated: () => {
        logSecurityEvent('subscription_activated', {
            timestamp: Date.now(),
        });
    },

    expired: () => {
        logSecurityEvent('subscription_expired', {
            timestamp: Date.now(),
        });
    },
};

// ======================== SECURITY EVENTS ========================

export const SecurityEvents = {
    cspViolation: (directive, blockedUri) => {
        logSecurityEvent('csp_violation', {
            directive,
            blocked_uri: blockedUri,
            timestamp: Date.now(),
        });
    },

    invalidQrFormat: () => {
        logSecurityEvent('invalid_qr_format_detected', {
            timestamp: Date.now(),
        });
    },

    sessionExpired: () => {
        logSecurityEvent('session_expired', {
            timestamp: Date.now(),
            ...getSessionFingerprint(),
        });
    },
};

// ======================== DEBUG CONSOLE OVERLAY ========================
// [DEV] Floating panel showing recent security events in real-time.
// Only renders in development mode. Draggable and collapsible.

export function DebugConsole() {
    const [isOpen, setIsOpen] = useState(false);
    const [events, setEvents] = useState([]);
    const [position, setPosition] = useState({ x: 16, y: 16 });
    const isDragging = useRef(false);
    const dragOffset = useRef({ x: 0, y: 0 });

    // Poll the event buffer for updates
    useEffect(() => {
        if (IS_PRODUCTION) return;
        const interval = setInterval(() => {
            setEvents([...DEBUG_EVENT_BUFFER]);
        }, 1000);
        return () => clearInterval(interval);
    }, []);

    const handleMouseDown = useCallback((e) => {
        isDragging.current = true;
        dragOffset.current = { x: e.clientX - position.x, y: e.clientY - position.y };
    }, [position]);

    useEffect(() => {
        const handleMouseMove = (e) => {
            if (!isDragging.current) return;
            setPosition({ x: e.clientX - dragOffset.current.x, y: e.clientY - dragOffset.current.y });
        };
        const handleMouseUp = () => { isDragging.current = false; };
        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mouseup', handleMouseUp);
        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        };
    }, []);

    if (IS_PRODUCTION) return null;

    if (!isOpen) {
        return (
            <button
                onClick={() => setIsOpen(true)}
                style={{
                    position: 'fixed', bottom: 16, right: 16, zIndex: 99999,
                    width: 44, height: 44, borderRadius: '50%',
                    background: '#F59E0B', color: '#0F172A', border: 'none',
                    cursor: 'pointer', fontSize: 18, fontWeight: 'bold',
                    boxShadow: '0 4px 12px rgba(245,158,11,0.4)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
                title="Open Debug Console"
            >
                🛡️
            </button>
        );
    }

    return (
        <div
            style={{
                position: 'fixed', left: position.x, top: position.y, zIndex: 99999,
                width: 380, maxHeight: 420, background: '#0F172A',
                border: '1px solid #334155', borderRadius: 12,
                boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
                fontFamily: "'Inter', monospace", fontSize: 11,
                display: 'flex', flexDirection: 'column', overflow: 'hidden',
            }}
        >
            {/* Header */}
            <div
                onMouseDown={handleMouseDown}
                style={{
                    padding: '8px 12px', background: '#1E293B',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    cursor: 'grab', borderBottom: '1px solid #334155', userSelect: 'none',
                }}
            >
                <span style={{ color: '#F59E0B', fontWeight: 700 }}>🛡️ Security Events ({events.length})</span>
                <button onClick={() => setIsOpen(false)} style={{ background: 'none', border: 'none', color: '#94A3B8', cursor: 'pointer', fontSize: 14 }}>✕</button>
            </div>
            {/* Event List */}
            <div style={{ flex: 1, overflowY: 'auto', padding: 8 }}>
                {events.length === 0 ? (
                    <p style={{ color: '#475569', textAlign: 'center', padding: 16 }}>No events yet</p>
                ) : (
                    events.map(evt => (
                        <div key={evt.id} style={{ padding: '6px 8px', marginBottom: 4, borderRadius: 6, background: '#1E293B', borderLeft: '3px solid #F59E0B' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
                                <span style={{ color: '#F59E0B', fontWeight: 600 }}>{evt.event}</span>
                                <span style={{ color: '#475569' }}>{evt.timestamp}</span>
                            </div>
                            <div style={{ color: '#94A3B8', wordBreak: 'break-all' }}>
                                {JSON.stringify(evt.params, null, 0).substring(0, 120)}
                            </div>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
}
